import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal"
import Select from "components/select";
import { ISimpleEventDetail, ShowTime, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

interface IDuplicateTicketModalProps extends IModalProps {
  onSubmit: (data: any) => void;
  showtimes: ShowTime[];
  eventDetailData: ISimpleEventDetail;
}

const DuplicateTicketSchema = (languages: SupportedLanguage[]) => {
  return z.object({
    showtime: z.string().min(1, "This field is required"),
  })
};

interface IAddTicketForm {
  showtime: string;
}

export const DuplicateSubTicketModal = ({onClose, open, onSubmit, showtimes, eventDetailData}: IDuplicateTicketModalProps) => {

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IAddTicketForm>({
    defaultValues: {
      showtime: ""
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        DuplicateTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },    
  });
  const _submit = (values: IAddTicketForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  useEffect(() => {
    if (open) {
      reset({
        showtime: ""
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  
  const showtimeArr = showtimes.map(showtime => ({
    label: get(showtime, "time", ""),
    value: get(showtime, "id", "")
  }))
  
  return (
    <Modal title="Please choose the new showtime when duplicating" onClose={onClose} open={open} closeOnClickBackdrop={false}>
      <div className="flex flex-col gap-y-4 box-container w-full">
        <form onSubmit={handleSubmit(_submit)} className="grid sm:grid-cols-6 gap-y-4 gap-x-4">
          <div className="col-span-full">
            <Label
              id="showtime"
              name="Showtime"
              required
            />
            <Controller
              control={control}
              name="showtime"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key="showtime"
                    inputRef={ref}
                    options={showtimeArr}
                    id="showtime"
                    placeholder="Select showtime"
                    defaultValue={
                      showtimeArr.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={showtimeArr.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `showtime`, "")}
                  />
                );
              }}
            />
            {get(
              errors,
              `showtime`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `showtime.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>

          <div className="col-span-full flex items-center gap-x-4 mt-4">
            <div className="flex-1">
              <Button
                type="button"
                data-autofocus
                onClick={onClose}
                className="inline-flex w-full justify-center bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-300"
              >
                Cancel
              </Button>
            </div>
            <div className="flex-1">
              <Button type="submit" className="w-full">
                Duplicate
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}